// src/components/LegalPage.js
// Public page for a legal document (Terms & Conditions or Privacy Policy).
// The admin uploads a PDF (stored on LandingContent.legal.*), and the server
// extracts its plain text via /api/legal/:type — we render that text here so
// the page reads as normal web content, not a native browser PDF viewer.
import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import logo from '../assets/logo.png';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CONFIG = {
  terms: { title: 'Terms & Conditions' },
  privacy: { title: 'Privacy Policy' },
};

export default function LegalPage({ type }) {
  const navigate = useNavigate();
  const { title } = CONFIG[type] || CONFIG.terms;
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/legal/${type}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setText((data && data.text) || '');
        setUrl((data && data.url) || '');
      })
      .catch(() => { if (!cancelled) { setText(''); setUrl(''); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafafa' }}>
      {/* Top bar */}
      <Box sx={{ py: 3, borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box component="img" src={logo} alt="Rewarding" sx={{ height: 36 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#111', letterSpacing: '-0.02em' }}>
              Rewarding
            </Typography>
          </Box>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#666' }} aria-label="Back to home">
            <ArrowBackIcon />
          </IconButton>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#111', letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
          {url ? (
            <Button
              variant="text"
              size="small"
              endIcon={<OpenInNewIcon />}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', color: '#888' }}
            >
              Download PDF
            </Button>
          ) : null}
        </Box>

        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : text ? (
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #eee',
              borderRadius: 3,
              p: { xs: 3, md: 5 },
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '1rem',
              lineHeight: 1.8,
              color: '#333',
              fontFamily: 'inherit',
            }}
          >
            {text}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center', color: '#999' }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 56, mb: 2, color: '#ccc' }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              {title} not available yet
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 380 }}>
              This document hasn't been published yet. Please check back soon.
            </Typography>
            <Button onClick={() => navigate('/')} sx={{ mt: 3, textTransform: 'none' }} variant="contained">
              Back to home
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
